import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ENV_GROUP_LABEL,
  effectiveFix,
  useDeviceGroups,
  useDevices,
  useMe,
  usePublishedReleases,
  useTenants,
  type EnvGroup,
  type GeoFix,
} from '../api/hooks';
import {
  DeviceFilters,
  hasActiveFilters,
  type GroupFilter,
  type OnlineFilter,
} from '../components/DeviceFilters';
import {
  addressWithoutVenue,
  fmtAgo,
  humanisePlaceType,
  useNow,
} from '../components/format';
import { Skeleton } from '../components/Skeleton';
import { sortIndicator, useTableSearch } from '../components/useTableSearch';
import { releaseCell } from '../lib/osVersion';
import { NO_RELEASE_LABEL, describeReleaseSource } from '../lib/releaseId';

type SortKey = 'thing' | 'friendly_name' | 'tenant_id' | 'group' | 'serial' | 'last_seen' | 'location' | 'release';

/**
 * The Location column's sortable/searchable text (DE-170). Prefers the venue
 * name, falling back to the address — the OS/Mender version this column
 * replaced lives on the device detail page and the Releases page, whereas
 * "where is this unit" had no fleet-wide view at all.
 *
 * Goes through effectiveFix so a device whose latest resolve failed still
 * reports its last verified whereabouts rather than dropping to blank.
 */
const locationText = (d: { geo: GeoFix | null }): string | null => {
  const fix = effectiveFix(d.geo);
  return (
    fix?.place_name ?? humanisePlaceType(fix?.place_type) ?? fix?.address ?? null
  );
};

export function Dashboard() {
  const me = useMe();
  const devices = useDevices();
  // DE-318 — which OS each published release names, so a pending row can be
  // told apart from one the device has already moved past.
  const published = usePublishedReleases();
  const navigate = useNavigate();
  // DE-147 — env-group membership for the Group column. Super-admin only;
  // tenant users get 403 and the column shows "—" for all rows.
  const groups = useDeviceGroups();
  // Tenants list — populates the Tenant filter dropdown. Super-admin only;
  // for tenant users the dropdown only shows "All tenants" (their RLS
  // scope already constrains them to one tenant, so a filter is moot).
  const tenants = useTenants();
  const [groupFilter, setGroupFilter] = useState<GroupFilter>('all');
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  const [onlineFilter, setOnlineFilter] = useState<OnlineFilter>('all');
  // DE-134 — refresh fmtAgo() output on a 30 s ticker so the "Last seen"
  // column stays accurate when an offline device's last_seen value stops
  // moving and React Query's structural sharing skips re-renders. List
  // precision is per-minute, so 30 s is plenty.
  useNow(30_000);

  // DE-134 — `online` is computed server-side from MQTT lifecycle events
  // plus a 15-min last_seen freshness gate. Replaces the client-side
  // last_seen < 5 min inference that read as "demo active" even for
  // long-offline devices.
  const onlineCount = (devices.data?.devices ?? []).filter((d) => d.online).length;
  const totalCount = devices.data?.devices.length ?? 0;

  // DE-147 — thing → env-group map. Same pattern as OpsDevices.
  const groupOf = new Map<string, EnvGroup>();
  for (const card of groups.data?.groups ?? []) {
    for (const thing of card.devices) groupOf.set(thing, card.name);
  }

  // Apply group + tenant + online filters BEFORE handing rows to the
  // search/sort hook so the "X of N match" count reflects the filtered
  // view, not the full fleet. Mirrors OpsDevices.
  const filteredRows = useMemo(() => {
    const all = devices.data?.devices ?? [];
    return all.filter((d) => {
      if (groupFilter !== 'all' && groupOf.get(d.thing) !== groupFilter) return false;
      if (tenantFilter !== 'all' && d.tenant_id !== tenantFilter) return false;
      if (onlineFilter === 'online' && !d.online) return false;
      if (onlineFilter === 'offline' && d.online) return false;
      return true;
    });
    // groupOf is rebuilt every render from groups.data — depend on its source
    // to avoid stale-closure issues.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices.data, groups.data, groupFilter, tenantFilter, onlineFilter]);

  const table = useTableSearch(filteredRows, {
    searchAccessors: [
      (d) => d.thing,
      (d) => d.friendly_name,
      (d) => d.tenant_id,
      (d) => {
        const g = groupOf.get(d.thing);
        return g ? ENV_GROUP_LABEL[g] : null;
      },
      (d) => d.serial,
      (d) => locationText(d),
      (d) => d.release_id,
      (d) => d.release_pending_id ?? null,
    ],
    sortAccessors: {
      thing: (d) => d.thing,
      friendly_name: (d) => d.friendly_name,
      tenant_id: (d) => d.tenant_id,
      group: (d) => groupOf.get(d.thing) ?? null,
      serial: (d) => d.serial,
      // Sort by last_seen as epoch-ms; null devices sort to the end.
      last_seen: (d) => (d.last_seen ? new Date(d.last_seen).getTime() : null),
      location: (d) => locationText(d),
      // DE-223 — sort/search by the public release ID; unlabelled devices sort last.
      release: (d) => d.release_id ?? d.release_pending_id ?? null,
    } satisfies Record<SortKey, (d: NonNullable<typeof devices.data>['devices'][number]) => string | number | null>,
    // Default = Thing ascending. Stable lexicographic order that doesn't
    // shift as devices come on/off line. The `as const` keeps TS from
    // narrowing K to just 'thing' (which would reject the other keys).
    initialSort: { key: 'thing' as SortKey, dir: 'asc' as const },
  });
  const headers: Array<{ key: SortKey; label: string; className?: string }> = [
    { key: 'thing', label: 'Thing' },
    { key: 'friendly_name', label: 'Name' },
    { key: 'tenant_id', label: 'Tenant' },
    { key: 'group', label: 'Group' },
    { key: 'serial', label: 'Serial' },
    { key: 'last_seen', label: 'Last seen' },
    { key: 'location', label: 'Location' },
    { key: 'release', label: 'Release' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted">
          {me.data?.scope === 'all'
            ? 'Cross-tenant view — every device in the fleet.'
            : `Your tenant: ${me.data?.tenant_id ?? '…'}`}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="card">
          <div className="text-xs text-muted">Devices</div>
          <div className="text-3xl font-semibold tabular-nums">{totalCount}</div>
        </div>
        <div className="card">
          <div className="text-xs text-muted">Online (last 5 min)</div>
          <div className="text-3xl font-semibold tabular-nums text-accent">{onlineCount}</div>
        </div>
        <div className="card">
          <div className="text-xs text-muted">Offline</div>
          <div className="text-3xl font-semibold tabular-nums text-muted">
            {totalCount - onlineCount}
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="font-medium mb-3">Devices</h2>
        {devices.isLoading && (
          <table className="w-full text-sm" aria-label="Loading devices">
            <tbody>
              <Skeleton.TableRows rows={5} cols={9} />
            </tbody>
          </table>
        )}
        {devices.error && (
          <p className="text-sm text-warn">
            Failed to load devices: {(devices.error as Error).message}
          </p>
        )}
        {devices.data && devices.data.devices.length === 0 && (
          <div className="text-center py-10 space-y-1">
            <p className="text-sm text-fg font-medium">No devices yet</p>
            <p className="text-xs text-muted">
              New devices appear here as soon as they connect to the cloud.
            </p>
          </div>
        )}
        {devices.data && devices.data.devices.length > 0 && (
          <>
            <DeviceFilters
              search={table.search}
              setSearch={table.setSearch}
              groupFilter={groupFilter}
              setGroupFilter={setGroupFilter}
              tenantFilter={tenantFilter}
              setTenantFilter={setTenantFilter}
              onlineFilter={onlineFilter}
              setOnlineFilter={setOnlineFilter}
              tenants={tenants.data?.tenants ?? []}
              showTenant={me.data?.scope === 'all'}
              searchPlaceholder="Search thing, name, tenant, group, serial, location, release…"
              refresh={() => void devices.refetch()}
              isFetching={devices.isFetching}
              endSlot={
                hasActiveFilters(
                  table.search,
                  groupFilter,
                  tenantFilter,
                  onlineFilter,
                ) && (
                  <span className="text-xs text-muted">
                    {table.rows.length} of {devices.data.devices.length} match
                  </span>
                )
              }
            />
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted border-b border-border">
                  {headers.map((h) => (
                    <th
                      key={h.key}
                      className="font-medium py-2 pr-4 cursor-pointer select-none hover:text-fg"
                      onClick={() => table.toggleSort(h.key)}
                    >
                      {h.label}{sortIndicator(table.sort, h.key)}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {table.rows.map((d) => {
                  const href = `/devices/${encodeURIComponent(d.thing)}`;
                  const go = () => navigate(href);
                  return (
                    <tr
                      key={d.thing}
                      onClick={go}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          go();
                        }
                      }}
                      tabIndex={0}
                      role="link"
                      aria-label={`Open ${d.friendly_name ?? d.thing}`}
                      className={`group/row border-b border-border last:border-0 cursor-pointer transition-colors hover:bg-soft focus:outline-none focus-visible:bg-soft focus-visible:ring-1 focus-visible:ring-accent ${d.online ? '' : 'opacity-60'}`}
                    >
                      <td className="py-2 pr-4 font-mono text-xs">{d.thing}</td>
                      <td className="py-2 pr-4 text-xs">
                        {d.friendly_name ?? <span className="text-muted">—</span>}
                      </td>
                      <td className="py-2 pr-4">
                        <span className="pill-muted">{d.tenant_id}</span>
                      </td>
                      <td className="py-2 pr-4 text-xs">
                        {(() => {
                          const g = groupOf.get(d.thing);
                          return g ? <span className="pill-muted">{ENV_GROUP_LABEL[g]}</span> : '—';
                        })()}
                      </td>
                      <td className="py-2 pr-4 font-mono text-xs">{d.serial ?? '—'}</td>
                      <td className="py-2 pr-4 tabular-nums">{fmtAgo(d.last_seen)}</td>
                      <td className="py-2 pr-4 text-xs max-w-[15rem]">
                        {(() => {
                          const fix = effectiveFix(d.geo);
                          if (!fix) return <span className="text-muted">—</span>;
                          // Unverified = the latest resolve failed and this is
                          // the last known position; italic so it isn't read as
                          // current. Long addresses truncate with the full text
                          // on hover rather than widening the table.
                          const stale = !fix.verified;
                          // A named venue if we have one; otherwise the kind of
                          // place the device is inside ("Conference centre"),
                          // which is all the open data offers for some venues.
                          const venue =
                            fix.place_name ?? humanisePlaceType(fix.place_type);
                          if (!venue && !fix.address) {
                            return (
                              <span
                                className={'text-muted tabular-nums' + (stale ? ' italic' : '')}
                                title={stale ? 'Last verified position' : undefined}
                              >
                                {fix.lat.toFixed(3)}, {fix.lon.toFixed(3)}
                              </span>
                            );
                          }
                          // Geoapify's address usually repeats the venue name;
                          // strip it so the second line adds information.
                          const addr = addressWithoutVenue(fix.address, fix.place_name);
                          return (
                            <div
                              className={stale ? 'italic' : undefined}
                              title={
                                [venue, fix.address].filter(Boolean).join(' — ') +
                                (stale ? ' (last verified position)' : '')
                              }
                            >
                              {venue && (
                                <div
                                  className={
                                    'truncate' + (fix.place_name ? '' : ' text-muted')
                                  }
                                >
                                  {venue}
                                </div>
                              )}
                              {addr && (
                                <div className={'truncate' + (venue ? ' text-muted' : '')}>
                                  {addr}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      {/* DE-223 — public release ID. A device that has never had
                          a published release is the NORMAL case, so this reads as
                          a plain dash rather than anything alarming. `pending`
                          means a release is staged but its parts aren't applied
                          yet, which is worth distinguishing from "none" — unless
                          the device's OS has already overtaken it (DE-318), when
                          it is superseded: nothing is waiting to apply. */}
                      <td className="py-2 pr-4 text-xs whitespace-nowrap">
                        {(() => {
                          const cell = releaseCell(d, published.data?.releases);
                          switch (cell.kind) {
                            case 'released':
                              return (
                                <span
                                  className="font-mono"
                                  title={describeReleaseSource(cell.source) ?? undefined}
                                >
                                  {cell.id}
                                  {cell.source === 'factory' && (
                                    <span className="ml-1 text-muted" title="Shipped with the image; no Portal release since">
                                      (image)
                                    </span>
                                  )}
                                </span>
                              );
                            case 'superseded':
                              return (
                                <span
                                  className="text-muted"
                                  title={`The device has moved on to ${cell.runningOs}, newer than the ${cell.namedOs} this release names, so it will never report this release. Nothing is waiting to apply.`}
                                >
                                  <span className="font-mono">{cell.releaseId}</span>{' '}
                                  <span className="italic">superseded</span>
                                </span>
                              );
                            case 'staged':
                              return (
                                <span className="text-muted italic" title="A release is staged but not applied yet">
                                  {cell.releaseId && (
                                    <span className="font-mono not-italic">{cell.releaseId} </span>
                                  )}
                                  staged
                                </span>
                              );
                            default:
                              return (
                                <span className="text-muted" title={NO_RELEASE_LABEL}>
                                  —
                                </span>
                              );
                          }
                        })()}
                      </td>
                      <td
                        className="py-2 pr-1 text-right text-muted opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-visible/row:opacity-100"
                        aria-hidden="true"
                      >
                        ›
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
